import type { Subscription, SubscriptionProductPrice, Team } from "@/types";

// biome-ignore lint/style/noEnum: Strict property handling
export enum SubscriptionLevelMap {
  Free = "free",
  Pro = "pro",
  Teams = "teams",
  Enterprise = "enterprise",
}

// biome-ignore lint/style/noEnum: Strict property handling
export enum SubscriptionIntervalMap {
  Month = "month",
  Year = "year",
}

// N.B.: Frontend equivalent of the backend enum with the same name
// biome-ignore lint/style/noEnum: Strict property handling
export enum SubscriptionLevelLimitation {
  CanAddUserToTeam = "can_add_user_to_team",
  CanAddClientSession = "can_add_client_session",
  CanAddCustomTemplate = "can_add_custom_template",
  CanShareDocuments = "can_share_documents",
  CanAccessTeamSettings = "can_access_team_settings", // Also needs to be admin
  HasPrioritySupport = "has_priority_support",
  HasApiAccess = "has_api_access",
  HasAnalyticTools = "has_analytic_tools",
  HasPriorityOnboardingAndTraining = "has_priority_onboarding_and_training",
  HasDedicatedAccountManager = "has_dedicated_account_manager",
}

export type SubscriptionGateResult = {
  name: string;
  passes: boolean;
  can_upgrade: boolean;
  reason: string | null;
  value: string | number | null | UsageData;
};

export type SubscriptionPlanLimits = {
  plan_limits: {
    can_add_user_to_team: SubscriptionGateResult;
    can_add_client_session: SubscriptionGateResult;
    can_add_custom_template: SubscriptionGateResult;
    can_share_documents: SubscriptionGateResult;
    can_access_team_settings: SubscriptionGateResult;
    has_priority_support: SubscriptionGateResult;
    has_api_access: SubscriptionGateResult;
    has_analytic_tools: SubscriptionGateResult;
    has_priority_onboarding_and_training: SubscriptionGateResult;
    has_dedicated_account_manager: SubscriptionGateResult;
  };
};

export type UsageData = {
  used: number;
  limit: number;
  remaining: number;
};

export type SubscriptionLevel = {
  value: SubscriptionLevelMap;
  features: string[];
};

export type SubscriptionInterval = {
  value: SubscriptionIntervalMap;
  label: string;
  suffix: string;
};

export type FetchSubscriptionResponseData = {
  subscription: Subscription;
  team: Team;
  intervals: SubscriptionInterval[];
  product_prices: SubscriptionProductPrice[];
  levels: SubscriptionLevel[];
};

export type SubscriptionStoreState = {
  teamId: number | null;
  team: Team | null;
  intervals: SubscriptionInterval[];
  productPrices: SubscriptionProductPrice[];
  subscription: Subscription;
  levels: SubscriptionLevel[];
  initialStateIsLoading: boolean;
  upgradeIsLoading: boolean;
};

export type SubscriptionActions = {
  fetchSubscription: (teamId: number) => Promise<void>;
  resetState: () => void;
  setIsFetchingInitialSubscriptionState: (isLoading: boolean) => void;
  setUpgradeIsLoading: (isLoading: boolean) => void;
};

const emptySubscriptionGateResult = (name: string) => ({
  name,
  passes: false,
  can_upgrade: false,
  reason: null,
  value: null,
});

export const getInitialSubscriptionRules = () => ({
  can_add_user_to_team: emptySubscriptionGateResult(
    SubscriptionLevelLimitation.CanAddUserToTeam
  ),
  can_add_client_session: emptySubscriptionGateResult(
    SubscriptionLevelLimitation.CanAddClientSession
  ),
  can_add_custom_template: emptySubscriptionGateResult(
    SubscriptionLevelLimitation.CanAddCustomTemplate
  ),
  can_share_documents: emptySubscriptionGateResult(
    SubscriptionLevelLimitation.CanShareDocuments
  ),
  can_access_team_settings: emptySubscriptionGateResult(
    SubscriptionLevelLimitation.CanAccessTeamSettings
  ),
  has_priority_support: emptySubscriptionGateResult(
    SubscriptionLevelLimitation.HasPrioritySupport
  ),
  has_api_access: emptySubscriptionGateResult(
    SubscriptionLevelLimitation.HasApiAccess
  ),
  has_analytic_tools: emptySubscriptionGateResult(
    SubscriptionLevelLimitation.HasAnalyticTools
  ),
  has_priority_onboarding_and_training: emptySubscriptionGateResult(
    SubscriptionLevelLimitation.HasPriorityOnboardingAndTraining
  ),
  has_dedicated_account_manager: emptySubscriptionGateResult(
    SubscriptionLevelLimitation.HasDedicatedAccountManager
  ),
});

export type SubscriptionStore = SubscriptionStoreState & SubscriptionActions;
